import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp, faCheck } from '@fortawesome/free-solid-svg-icons';

import Field, { useFieldIds } from './Field.jsx';
import { menuStyle, useDismiss, usePlacement } from './menuPlacement.js';
import { inputPlaceholder, openingQuery } from './autocompleteText.js';

import './Select.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const shared = {
    /* For a control used inline, with no visible label to point at. */
    ariaLabel: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.node,
    labelEnd: PropTypes.node,
    helpText: PropTypes.node,
    status: PropTypes.oneOf(['neutral', 'danger']),
    isRequired: PropTypes.bool,
    isDisabled: PropTypes.bool,
    tooltip: PropTypes.string,
    placeholder: PropTypes.string,
    name: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.node,
        description: PropTypes.node,
        isDisabled: PropTypes.bool,
        isChecked: PropTypes.bool,
        // Extra class on the option row — lets a caller mark one (a non-selectable group header, say)
        // without the Select having to know what the grouping means.
        className: PropTypes.string,
    })),
    onChange: PropTypes.func,
};

/* ── Select ── */

export const Select = ({
    id,
    label,
    ariaLabel,
    labelEnd,
    helpText,
    status = 'neutral',
    isRequired = false,
    isDisabled = false,
    tooltip,
    placeholder = 'Select...',
    options = [],
    value,
    values = [],
    multiSelect = false,
    closeOnSelect = !multiSelect,
    name,
    onChange = () => {},
    fullWidth = false,
}) => {
    const { fieldId, messageId } = useFieldIds(id, !!helpText);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    const close = useCallback(() => setIsOpen(false), []);
    const placement = usePlacement(triggerRef, isOpen);
    useDismiss([triggerRef, menuRef], isOpen, close);

    const selected = useMemo(
        () => (multiSelect
            ? options.filter((o) => values.includes(o.value))
            : options.filter((o) => o.value === value)),
        [multiSelect, options, value, values],
    );

    const display = selected.length === 0
        ? placeholder
        : (multiSelect ? selected.map((o) => o.label).join(', ') : selected[0].label);

    const commit = (option) => {
        if (option.isDisabled) return;
        if (multiSelect) {
            const next = values.includes(option.value)
                ? values.filter((v) => v !== option.value)
                : [...values, option.value];
            onChange({ target: { name, value: next } });
            if (typeof closeOnSelect === 'function' ? closeOnSelect(next) : closeOnSelect) {
                close();
                triggerRef.current?.focus();
            }
            return;
        }
        onChange({ target: { name, value: option.value } });
        close();
        triggerRef.current?.focus();
    };

    const step = (delta) => {
        if (!options.length) return;
        let next = activeIndex;
        for (let i = 0; i < options.length; i += 1) {
            next = (next + delta + options.length) % options.length;
            if (!options[next].isDisabled) break;
        }
        setActiveIndex(next);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && isOpen) {
            event.preventDefault();
            close();
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!isOpen) {
                openMenu();
                return;
            }
            step(event.key === 'ArrowDown' ? 1 : -1);
            return;
        }
        // Enter closes the picker: it commits the active option when the menu is open, and
        // otherwise releases the trigger rather than leaving it focused.
        if (event.key === 'Enter') {
            event.preventDefault();
            if (!isOpen) {
                event.currentTarget.blur();
                return;
            }
            if (activeIndex >= 0) commit(options[activeIndex]);
            else close();
        }
    };

    return (
        <Field
            id={fieldId}
            label={label}
            labelEnd={labelEnd}
            helpText={helpText}
            status={status}
            isRequired={isRequired}
            isDisabled={isDisabled}
            tooltip={tooltip}
            className={fullWidth ? 'field--full' : ''}
        >
            <button
                ref={triggerRef}
                id={fieldId}
                type="button"
                className="field-box select-trigger"
                disabled={isDisabled}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-invalid={status === 'danger' || undefined}
                aria-describedby={messageId}
                onClick={() => setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
            >
                <span className={cx('select-value', selected.length === 0 && 'select-value--empty')}>
                    {display}
                </span>
                <FontAwesomeIcon icon={isOpen ? faAngleUp : faAngleDown} className="field-icon" />
            </button>

            {isOpen && placement && createPortal(
                <ul
                    ref={menuRef}
                    className="select-menu"
                    role="listbox"
                    aria-multiselectable={multiSelect || undefined}
                    style={menuStyle(placement)}
                >
                    {options.length === 0 && <li className="select-empty">No options</li>}
                    {options.map((option, index) => {
                        // isChecked: shown ticked without being a value of its own (e.g. a sub-geography
                        // included by its chosen parent); the trigger still lists only real values.
                        const isSelected = option.isChecked || selected.some((o) => o.value === option.value);
                        return (
                            <li
                                key={option.value}
                                role="option"
                                aria-selected={isSelected}
                                aria-disabled={option.isDisabled || undefined}
                                className={cx(
                                    'select-option',
                                    isSelected && 'select-option--selected',
                                    index === activeIndex && 'select-option--active',
                                    option.isDisabled && 'select-option--disabled',
                                    option.className,
                                )}
                                onMouseEnter={() => setActiveIndex(index)}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => commit(option)}
                            >
                                <span className="select-option-text">
                                    {option.label}
                                    {option.description && (
                                        <span className="select-option-description">{option.description}</span>
                                    )}
                                </span>
                                {isSelected && <FontAwesomeIcon icon={faCheck} className="select-option-check" />}
                            </li>
                        );
                    })}
                </ul>,
                document.body,
            )}
        </Field>
    );
};

Select.propTypes = {
    ...shared,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Multi-select selection. Emits the full next array through onChange.
    values: PropTypes.array,
    multiSelect: PropTypes.bool,
    // Close the menu after a pick. Defaults to true for a single select; a multi-select stays open (to
    // tick several). A function receives the next values and decides, e.g. "close once nothing is left".
    closeOnSelect: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    fullWidth: PropTypes.bool,
};

/* ── Autocomplete ── */

/**
 * A Select you can type into. Typing filters; only a listed option can be committed, so stray text
 * reverts to the current selection on blur.
 */
export const Autocomplete = ({
    id,
    label,
    ariaLabel,
    labelEnd,
    helpText,
    status = 'neutral',
    isRequired = false,
    isDisabled = false,
    tooltip,
    placeholder = 'Search...',
    options = [],
    value,
    name,
    onChange = () => {},
    // Every keystroke, with the raw typed text. onChange only fires when a value is committed (a pick,
    // Enter, or leaving a freeSolo field), so a form that clears a field's error as soon as it is edited
    // listens here.
    onInputChange,
    // Options are suggestions, not an allow-list: whatever the user types is itself
    // a valid value. Used by the free-text pickers (job title, email, org name).
    freeSolo = false,
    maxLength,
    maxInitialOptions = null,
    minCharsToFilter = null,
}) => {
    const { fieldId, messageId } = useFieldIds(id, !!helpText);
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    // Whether the user has typed since the field opened. Opening seeds `query` with the committed
    // value so the box does not blank out (see openMenu), which means a non-empty `query` no longer
    // implies an edit — this is what tells the two apart.
    const [dirty, setDirty] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const boxRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);
    // Set when Enter already committed the typed text, so the blur it triggers
    // doesn't fire onChange a second time.
    const rawCommittedRef = useRef(false);

    const close = useCallback(() => setIsOpen(false), []);
    const placement = usePlacement(boxRef, isOpen);
    useDismiss([boxRef, menuRef], isOpen, close);

    const selected = options.find((o) => o.value === value) || null;

    // Closed, the input shows the committed label; open, it shows what is being typed.
    // Under freeSolo the committed value need not be in `options`, so show it verbatim.
    const committed = selected?.label
        ?? (freeSolo && value !== null && value !== undefined && value !== '' ? String(value) : '');
    const text = isOpen ? query : committed;

    // Lists of tens of thousands only render their first slice until enough characters are typed.
    const filtered = useMemo(() => {
        const capped = maxInitialOptions ? options.slice(0, maxInitialOptions) : options;
        // Until the user types, the seeded query is the CURRENT value, not a search — filtering on it
        // would narrow the list to the one option already chosen and hide every alternative.
        if (!isOpen || !dirty || !query) return capped;
        const pool = minCharsToFilter && query.length < minCharsToFilter ? capped : options;
        const q = query.toLowerCase();
        return pool.filter((o) => String(o.label).toLowerCase().includes(q));
    }, [isOpen, dirty, query, options, maxInitialOptions, minCharsToFilter]);

    let limitMessage = null;
    if (maxInitialOptions && !query && options.length > maxInitialOptions) {
        limitMessage = `Showing top ${maxInitialOptions} of ${options.length} options. Type to see more.`;
    } else if (minCharsToFilter && query && query.length < minCharsToFilter) {
        limitMessage = `Type ${minCharsToFilter} or more characters to search all options.`;
    }

    const commit = (option) => {
        if (!option || option.isDisabled) return;
        onChange({ target: { name, value: option.value } });
        setQuery('');
        setDirty(false);
        close();
    };

    // freeSolo only: commit the typed text, preferring a known option whose label
    // matches it so picking "Producer" by typing stores the same value as clicking it.
    const commitRaw = (raw) => {
        const trimmed = raw.trim();
        // Emptying the box clears the value. Previously blur only committed a NON-empty query, so a
        // free-text field could be typed into but never cleared — the old value came straight back.
        const match = trimmed
            ? options.find((o) => String(o.label).toLowerCase() === trimmed.toLowerCase())
            : null;
        rawCommittedRef.current = true;
        onChange({ target: { name, value: match ? match.value : trimmed } });
        setQuery('');
        setDirty(false);
        close();
    };

    /**
     * Open ready for the next keystroke.
     *
     * Free text (freeSolo) opens with the committed value in the box, ready to edit: blanking it meant
     * changing one character of a job title retyped the lot. A pick from a list opens EMPTY, as a select
     * box does, with the current choice kept as the placeholder: its label is not something to edit,
     * and leaving "Any country" in the box made every search start by deleting it. Nothing is cleared:
     * leaving without picking keeps the value it had.
     */
    const openMenu = () => {
        setQuery(openingQuery(freeSolo, committed));
        setDirty(false);
        setActiveIndex(-1);
        setIsOpen(true);
    };

    const handleBlur = () => {
        // `dirty` and not `query.trim()`: the seeded query is the value that is already committed, so
        // committing it again on a focus-and-leave would fire a spurious onChange on every visit.
        if (freeSolo && !rawCommittedRef.current && dirty) commitRaw(query);
        rawCommittedRef.current = false;
        setQuery('');
        setDirty(false);
        close();
    };

    // The caret toggles the list, the way clicking the Select trigger does. Focus is taken first so
    // an opened menu always has the input behind it; closing reuses the blur path so typed free-text
    // commits exactly as it would if the user had clicked away.
    const toggleOpen = () => {
        const wasOpen = isOpen;
        inputRef.current?.focus();
        if (wasOpen) handleBlur();
        else openMenu();
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && isOpen) {
            event.preventDefault();
            handleBlur();
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!isOpen) {
                setIsOpen(true);
                return;
            }
            if (!filtered.length) return;
            const delta = event.key === 'ArrowDown' ? 1 : -1;
            setActiveIndex((activeIndex + delta + filtered.length) % filtered.length);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            if (isOpen && activeIndex >= 0) commit(filtered[activeIndex]);
            else if (freeSolo && dirty) commitRaw(query);
            event.currentTarget.blur();
        }
    };

    return (
        <Field
            id={fieldId}
            label={label}
            labelEnd={labelEnd}
            helpText={helpText}
            status={status}
            isRequired={isRequired}
            isDisabled={isDisabled}
            tooltip={tooltip}
        >
            <div ref={boxRef} className="field-box">
                <input
                    ref={inputRef}
                    id={fieldId}
                    className="field-input"
                    type="text"
                    role="combobox"
                    autoComplete="off"
                    name={name}
                    placeholder={inputPlaceholder({
                        isOpen, freeSolo, committed, placeholder,
                    })}
                    disabled={isDisabled}
                    required={isRequired}
                    maxLength={maxLength}
                    value={text}
                    aria-label={ariaLabel}
                    aria-expanded={isOpen}
                    aria-invalid={status === 'danger' || undefined}
                    aria-describedby={messageId}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setDirty(true);
                        setActiveIndex(-1);
                        setIsOpen(true);
                        onInputChange?.(event.target.value);
                    }}
                    onFocus={openMenu}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                />
                {/* A real control, not decoration. The caret is a flex sibling of the input with its
                    own click area, so as a bare icon it swallowed the click: the input never
                    focused and the list never opened. mousedown is swallowed so the input keeps
                    focus and its blur cannot close the menu out from under the click. Out of the tab
                    order — the combobox input is the control, and ArrowDown opens from there. */}
                <button
                    type="button"
                    className="field-action"
                    tabIndex={-1}
                    disabled={isDisabled}
                    aria-label={isOpen ? 'Close options' : 'Open options'}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={toggleOpen}
                >
                    <FontAwesomeIcon icon={isOpen ? faAngleUp : faAngleDown} className="field-icon" />
                </button>
            </div>

            {isOpen && placement && createPortal(
                <ul ref={menuRef} className="select-menu" role="listbox" style={menuStyle(placement)}>
                    {filtered.length === 0 && <li className="select-empty">No matches</li>}
                    {filtered.map((option, index) => (
                        <li
                            key={option.value}
                            role="option"
                            aria-selected={option.value === value}
                            aria-disabled={option.isDisabled || undefined}
                            className={cx(
                                'select-option',
                                option.value === value && 'select-option--selected',
                                index === activeIndex && 'select-option--active',
                                option.isDisabled && 'select-option--disabled',
                                option.className,
                            )}
                            onMouseEnter={() => setActiveIndex(index)}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => commit(option)}
                        >
                            <span className="select-option-text">
                                {option.label}
                                {option.description && (
                                    <span className="select-option-description">{option.description}</span>
                                )}
                            </span>
                            {option.value === value && <FontAwesomeIcon icon={faCheck} className="select-option-check" />}
                        </li>
                    ))}
                    {limitMessage && <li className="select-empty">{limitMessage}</li>}
                </ul>,
                document.body,
            )}
        </Field>
    );
};

Autocomplete.propTypes = {
    ...shared,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    freeSolo: PropTypes.bool,
    maxLength: PropTypes.number,
    maxInitialOptions: PropTypes.number,
    minCharsToFilter: PropTypes.number,
};
